import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExEmptyState,
    ExLoader,
} from '@boomi/exosphere';
import { type IdentityProvider, fetchIdentityProviders } from './identityProviders';
import IdentityProviderRow from './IdentityProviderRow';
import styles from '../shared/List.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

const IdentityProviderList = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<IdentityProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchIdentityProviders()
            .then((response) => setItems(response.results))
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <h2 className={`${styles.heading} ex-text-h2`}>Identity Providers</h2>
                    {items.length > 0 && (
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={() => navigate('/identity-providers/new')}
                        >
                            Create Identity Provider
                        </ExButton>
                    )}
                </div>
            </div>
            {error ? (
                <ExAlertBanner type={AlertBannerType.ERROR} open onClose={() => setError(null)}>
                    {error}
                </ExAlertBanner>
            ) : items.length === 0 ? (
                loading ? (
                    <ExLoader label="Loading identity providers…" />
                ) : (
                    <ExEmptyState
                        className={styles.empty}
                        label="No identity providers configured"
                        text="Add an identity provider to authenticate connections to your connectors."
                    >
                        <div slot="header">
                            <img src="/emptyStateIcon.svg" alt="No identity providers configured" />
                        </div>
                        <ExButton
                            slot="action"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={() => navigate('/identity-providers/new')}
                        >
                            Create Identity Provider
                        </ExButton>
                    </ExEmptyState>
                )
            ) : (
                <div className={styles.list}>
                    {items.map((idp) => (
                        <IdentityProviderRow
                            key={idp.id}
                            idp={idp}
                            onSelect={(id) => navigate(`/identity-providers/${id}`)}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

export default IdentityProviderList;
